import * as Accordion from "@radix-ui/react-accordion";
import { NATURE_CLUB_LP_URL } from "@sa-nu/shared-utils/url";
import clsx from "clsx";

import { AreasColumn } from "./areas-column";
import MenuDivider from "./menu-divider";
import MenuItem, { MenuAccordionItem } from "./menu-item";
import MenuLayoutGroup from "./menu-layout-group";

// MenuLayoutGroup: 背景色の異なる、レイアウト上のまとまり
// 2パターン存在:
//  1. "menuGroups": 子メニューを束ねるグループの配列 （例: 困ったときは、メンバー特典、四季に暮らす、エリア）
//  2. "links": リンクメニュー（子なし）の配列（例: [建築・施設、アカウント情報、お知らせ、LOG OUT]）
// レイアウト上、1つのMenuLayoutGroup内にMenuGroupとMenuLinkが混在することがないため、描画時にも明確に区別するためにキーで分けている。
type MenuLayoutGroup =
  | {
      menuGroups: MenuGroup[];
      textColor?: string;
      bgColor?: string;
    }
  | {
      links: MenuLink[];
      textColor?: string;
      bgColor?: string;
    };

// 子メニューをもつグループ （例: 困ったときは、メンバー特典、四季に暮らす、エリア）
export type MenuGroup = {
  title: string;
  iconPath: string;
  items: MenuGroupItem[] | "fetchAreas";
  href?: string;
};

// 子メニュー （例: よくあるお問い合わせ、サポートセンターへ連絡する、予約ルールに関するヘルプ）
type MenuGroupItem =
  | {
      name: string;
      href: string;
      isExternal?: boolean;
    }
  | "divider";

// 子なしリンクメニュー（例: 建築・施設、ログイン / ログアウト）
type MenuLink = {
  title: string;
  href: string;
  iconPath?: string;
};

////// メニュー構造定義
// ログインメニュー
const MENU_GROUP_GUIDE: MenuGroup = {
  title: "Stay Guide",
  iconPath: "/i/icon_guide.svg",
  items: [
    {
      name: "滞在ガイド",
      href: "https://www.sa-nu.com/magazine/guide-stay/welcome",
      isExternal: true,
    },
    {
      name: "冬の滞在ガイド",
      href: "https://www.2ndhome-articles.sa-nu.com/stay-guide-winter26",
      isExternal: true,
    },
    {
      name: "設備操作マニュアル",
      href: "https://sanu.notion.site/1efe6f3bdd45487092b6178464be1ba8?v=234d14f248a8808d8559000c4dee4cf6",
      isExternal: true,
    },
    {
      name: "備品リスト",
      href: "https://2ndhome.sa-nu.com/supplies_list_new/sanucabin",
    },
  ],
};

const MENU_GROUP_SUPPORT: MenuGroup = {
  title: "Support",
  iconPath: "/i/icon_help.svg",
  items: [
    {
      name: "ヘルプセンター",
      href: "https://help.sa-nu.com/hc/ja",
      isExternal: true,
    },
    {
      name: "お問い合わせフォーム",
      href: "https://help.sa-nu.com/hc/ja/requests/new",
      isExternal: true,
    },
    {
      name: "Beginner's Guide",
      href: "https://2ndhome.sa-nu.com/beginners_guide",
    },
  ],
};

const MENU_GROUP_MEMBER_BENEFITS: MenuGroup = {
  title: "メンバー特典",
  iconPath: "/i/icon_star_circle.svg",
  items: [
    {
      name: "カーシェアサポート",
      href: "https://www.2ndhome-articles.sa-nu.com/car-share",
      isExternal: true,
    },
    {
      name: "キッズケア",
      href: "https://sanu.notion.site/KIDS-CARE-98e5c0bd1fbc4bb1a9c6b6b2d55b89a0",
      isExternal: true,
    },
    { name: "One more Cabin", href: "/one-more-cabin" },
    { name: "Stay Gift", href: "/stay-gift" },
    { name: "Nature Club", href: NATURE_CLUB_LP_URL, isExternal: true },
  ],
};

const MENU_GROUP_AREA: MenuGroup = {
  title: "エリア",
  href: "https://2ndhome.sa-nu.com/area",
  iconPath: "/i/icon_location.svg",
  items: "fetchAreas",
};

const MENU_OTHER_LINKS: MenuLink[] = [
  {
    title: "建築・施設",
    href: "/architectures",
    iconPath: "/i/icon_architecture.svg",
  },
  {
    title: "アカウント情報",
    href: "/member/profile",
    iconPath: "/i/icon_person.svg",
  },
  { title: "お知らせ", href: "/news", iconPath: "/i/icon_news.svg" },
  { title: "LOG OUT", href: "/auth/logout" },
];

const MENU_GROUP_FOR_BUSINESS: MenuGroup = {
  title: "SANU 2nd Home for Business",
  iconPath: "/i/icon_business.svg",
  items: [
    {
      name: "ダッシュボード",
      href: "/business/manager",
    },
    {
      name: "組織設定",
      href: "/business/manager/settings",
    },
    {
      name: "ご請求・領収書",
      href: "/business/manager/payments",
    },
    {
      name: "メンバー管理",
      href: "/business/manager/members",
    },
    {
      name: "宿泊管理",
      href: "/business/manager/reservations",
    },
  ],
};

export const MENU_MOBILE_LOGGED_IN: MenuLayoutGroup[] = [
  { menuGroups: [MENU_GROUP_GUIDE] },
  { menuGroups: [MENU_GROUP_SUPPORT] },
  { menuGroups: [MENU_GROUP_MEMBER_BENEFITS] },
  { menuGroups: [MENU_GROUP_AREA] },
  { links: MENU_OTHER_LINKS },
];

export const MENU_DESKTOP_LOGGED_IN: MenuLayoutGroup[] = [
  { menuGroups: [MENU_GROUP_GUIDE, MENU_GROUP_SUPPORT] },
  { menuGroups: [MENU_GROUP_MEMBER_BENEFITS] },
  { menuGroups: [MENU_GROUP_AREA] },
  { links: MENU_OTHER_LINKS },
];

// 未ログイン時のメニュー。アカウント情報 / お知らせは出さず、末尾はログイン導線。
const MENU_GUEST_OTHER_LINKS: MenuLink[] = [
  {
    title: "建築・施設",
    href: "/architectures",
    iconPath: "/i/icon_architecture.svg",
  },
  { title: "ログイン", href: "/login", iconPath: "/i/icon_key.svg" },
];

const MENU_DESKTOP_GUEST: MenuLayoutGroup[] = [
  { menuGroups: [MENU_GROUP_GUIDE, MENU_GROUP_SUPPORT] },
  { menuGroups: [MENU_GROUP_MEMBER_BENEFITS] },
  { menuGroups: [MENU_GROUP_AREA] },
  { links: MENU_GUEST_OTHER_LINKS },
];

const MENU_MOBILE_GUEST: MenuLayoutGroup[] = [
  { menuGroups: [MENU_GROUP_GUIDE] },
  { menuGroups: [MENU_GROUP_SUPPORT] },
  { menuGroups: [MENU_GROUP_MEMBER_BENEFITS] },
  { menuGroups: [MENU_GROUP_AREA] },
  { links: MENU_GUEST_OTHER_LINKS },
];

export function Menu({
  areas,
  isMobile,
  isLoggedIn,
  showOrganizationManagerMenu = false,
}: {
  areas: { id: string; name: string; slug: string }[];
  isMobile: boolean;
  isLoggedIn: boolean;
  showOrganizationManagerMenu?: boolean;
}) {
  // 未ログインのみ表示項目を変更。ログイン済みはプランによらず従来メニュー。
  let menuLayoutGroups = isLoggedIn
    ? isMobile
      ? MENU_MOBILE_LOGGED_IN
      : MENU_DESKTOP_LOGGED_IN
    : isMobile
      ? MENU_MOBILE_GUEST
      : MENU_DESKTOP_GUEST;
  if (showOrganizationManagerMenu) {
    const menuLayoutGroupsForBusiness = {
      menuGroups: [MENU_GROUP_FOR_BUSINESS],
      textColor: "text-white",
      bgColor: "bg-[#332f21]",
    };
    if (isMobile) {
      menuLayoutGroups = [menuLayoutGroupsForBusiness, ...menuLayoutGroups];
    } else {
      menuLayoutGroups = [...menuLayoutGroups, menuLayoutGroupsForBusiness];
    }
  }

  return (
    <Accordion.Root
      type="multiple"
      defaultValue={
        isMobile
          ? [
              // 未ログインではエリアを default で開く
              ...(!isLoggedIn ? [MENU_GROUP_AREA.title] : []),
              ...(showOrganizationManagerMenu
                ? [MENU_GROUP_FOR_BUSINESS.title]
                : []),
            ]
          : undefined
      }
      className={clsx(
        isMobile
          ? "divide-skogecha/6 flex w-full flex-col divide-y"
          : "flex flex-row",
      )}
    >
      {menuLayoutGroups.map((layoutGroup, i) => (
        <MenuLayoutGroup
          key={i}
          isMobile={isMobile}
          bgColor={layoutGroup.bgColor}
        >
          {"menuGroups" in layoutGroup ? (
            layoutGroup.menuGroups.map((group) => (
              <MenuGroup
                key={group.title}
                group={group}
                isMobile={isMobile}
                textColor={layoutGroup.textColor}
              >
                {group.items === "fetchAreas" ? (
                  <AreasColumn areas={areas} isMobile={isMobile} />
                ) : (
                  group.items.map((item, i) =>
                    item === "divider" ? (
                      <MenuDivider key={`${item}-${i}`} />
                    ) : (
                      <MenuItem
                        key={item.name}
                        {...item}
                        type={isMobile ? "mobileAccordionChild" : "desktopLink"}
                        textColor={layoutGroup.textColor}
                      >
                        {item.name}
                      </MenuItem>
                    ),
                  )
                )}
              </MenuGroup>
            ))
          ) : (
            <MenuLinks links={layoutGroup.links} isMobile={isMobile} />
          )}
        </MenuLayoutGroup>
      ))}
    </Accordion.Root>
  );
}

function MenuGroup({
  group,
  isMobile,
  children,
  textColor,
}: {
  group: MenuGroup;
  isMobile: boolean;
  children: React.ReactNode;
  textColor?: string;
}) {
  return isMobile ? (
    <MenuAccordionItem
      title={group.title}
      iconPath={group.iconPath}
      textColor={textColor}
    >
      {children}
    </MenuAccordionItem>
  ) : (
    <div className="flex flex-col justify-start gap-[24px] nth-2:mt-[48px]">
      <MenuItem icon={group.iconPath} href={group.href} textColor={textColor}>
        {group.title}
      </MenuItem>
      {children}
    </div>
  );
}

function MenuLinks({
  links,
  isMobile,
}: {
  links: MenuLink[];
  isMobile: boolean;
}) {
  return links.map((link) => (
    <MenuItem
      key={link.title}
      icon={link.iconPath}
      {...link}
      type={isMobile ? "mobileLink" : "desktopLink"}
    >
      {link.title}
    </MenuItem>
  ));
}
